import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  PlatformCatalogService,
  SearchProduct,
} from 'src/app/services/platform-catalog.service';

const HISTORY_KEY = 'universalbus.buy.search.history';
const MAX_HISTORY = 8;
const DEBOUNCE_MS = 120;

export interface TrendingItem {
  keyword: string;
  tag?: string;
  /** 展示标题（匹配到真实商品后使用商品名） */
  title?: string;
  /** 真实商品图标 fileid */
  icon?: string;
  /** 无图标时的占位字符 */
  fallback?: string;
}

/**
 * 商品搜索框（PC 下拉面板 / 移动端沉浸式面板共用）。
 * 页面头部会在 /buy 路由下渲染两个实例：mode="desktop" 与 mode="mobile"，
 * 由 CSS 按断点切换显示。
 */
@Component({
  selector: 'app-platform-search',
  templateUrl: './platform-search.component.html',
  styleUrls: ['./platform-search.component.scss'],
})
export class PlatformSearchComponent implements OnInit, OnDestroy {
  @Input() mode: 'desktop' | 'mobile' = 'desktop';

  /** 面板开关状态变化（遮罩由页面头部统一渲染，避免 toolbar 的 backdrop-filter 影响 fixed 定位） */
  @Output() openChange = new EventEmitter<boolean>();

  @ViewChild('searchInput', { static: false })
  searchInput?: ElementRef<HTMLInputElement>;

  open = false;
  query = '';
  results: SearchProduct[] = [];
  history: string[] = [];
  activeIndex = -1;

  /** 「大家都在搜」配置：维护这里即可，无需改动模板逻辑 */
  trendingKeywords: TrendingItem[] = [
    { keyword: 'YouTube' },
    { keyword: 'Netflix' },
    { keyword: 'ChatGPT Plus', tag: 'AI' },
    { keyword: 'Gemini', tag: 'AI' },
    { keyword: 'Disney+' },
  ];
  trending: TrendingItem[] = [];

  private debounceTimer: any = null;

  constructor(
    private catalog: PlatformCatalogService,
    private el: ElementRef
  ) {}

  ngOnInit(): void {
    this.history = this.loadHistory();
    // 数据加载完成后，把热搜词与真实商品对应起来（取真实 Logo / 名称）
    this.catalog
      .load()
      .then(() => this.buildTrending())
      .catch(() => this.buildTrending());
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /* ============ 面板开关 ============ */

  onFocus() {
    this.openPanel();
  }

  /** 点击输入框胶囊任意位置 → 聚焦并展开面板（桌面端「点击展开」） */
  onBoxClick() {
    this.searchInput?.nativeElement.focus();
  }

  /** 移动端返回按钮：阻止冒泡，避免触发输入框聚焦重新展开 */
  onBack(event: MouseEvent) {
    event.stopPropagation();
    this.close();
  }

  onInput() {
    this.openPanel();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => this.updateResults(), DEBOUNCE_MS);
  }

  private openPanel() {
    this.setOpen(true);
    this.history = this.loadHistory();
  }

  private setOpen(value: boolean) {
    if (this.open !== value) {
      this.open = value;
      this.openChange.emit(value);
    }
  }

  close() {
    this.setOpen(false);
    this.query = '';
    this.results = [];
    this.activeIndex = -1;
    this.searchInput?.nativeElement.blur();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.open && !this.el.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  /* ============ 搜索 ============ */

  private updateResults() {
    this.activeIndex = -1;
    this.results = this.catalog.search(this.query);
  }

  selectProduct(p: SearchProduct) {
    this.saveHistory(p.name);
    this.close();
    this.catalog.locatePlatform(p.id);
  }

  /** 点击热搜 / 历史词：一键直达对应商品；找不到时回退为普通搜索 */
  selectKeyword(kw: string) {
    const best = this.catalog.findBest(kw);
    if (best) {
      this.saveHistory(best.name);
      this.close();
      this.catalog.locatePlatform(best.id);
    } else {
      this.query = kw;
      this.updateResults();
    }
  }

  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (!this.query.trim() || !this.results.length) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
      this.scrollActiveIntoView();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.scrollActiveIntoView();
    } else if (e.key === 'Enter') {
      if (this.results.length === 1) {
        this.selectProduct(this.results[0]);
      } else if (this.activeIndex >= 0) {
        this.selectProduct(this.results[this.activeIndex]);
      }
    }
  }

  private scrollActiveIntoView() {
    setTimeout(() => {
      const item = this.el.nativeElement.querySelector(
        '.result-item.keyboard-active'
      );
      if (item?.scrollIntoView) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }

  /* ============ 热搜 ============ */

  private buildTrending() {
    this.trending = this.trendingKeywords.map((t) => {
      const best = this.catalog.findBest(t.keyword);
      if (best) {
        return {
          keyword: t.keyword,
          tag: t.tag,
          title: best.name,
          icon: best.icon,
          fallback: (best.name || '?').charAt(0).toUpperCase(),
        };
      }
      return {
        keyword: t.keyword,
        tag: t.tag,
        title: t.keyword,
        icon: '',
        fallback: (t.keyword || '?').charAt(0).toUpperCase(),
      };
    });
  }

  /* ============ 搜索历史 ============ */

  private loadHistory(): string[] {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr)
        ? arr.filter((x) => typeof x === 'string').slice(0, MAX_HISTORY)
        : [];
    } catch (error) {
      return [];
    }
  }

  private saveHistory(kw: string) {
    try {
      const value = (kw || '').trim();
      if (!value) {
        return;
      }
      const next = this.loadHistory().filter((x) => x !== value);
      next.unshift(value);
      if (next.length > MAX_HISTORY) {
        next.length = MAX_HISTORY;
      }
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      this.history = next;
    } catch (error) {}
  }

  removeHistory(kw: string, event?: MouseEvent) {
    event?.stopPropagation();
    try {
      const next = this.loadHistory().filter((x) => x !== kw);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      this.history = next;
    } catch (error) {}
  }

  clearHistory() {
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch (error) {}
    this.history = [];
  }
}
